import * as React from 'react';

import { 
    FinancialRecordStatus,
    PortalTextCommon,
} from '../../Models/Api/strongbox.financialportal';

import {
    Tooltip
} from '@mui/material';

import { BrandConfig } from '../../Store/Tenant';
import { LinkMode } from '../../Store/ImportFinancials';

import { InlineStylingValues } from '../../Utils/Constants';

import { SubmittedByIcon } from '../SubmittedByIcon/SubmittedByIcon';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTable,
    faQuestion,
    faThermometerEmpty,
    faExclamationTriangle,
    faTrash,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';

export type SubmissionStatusSummary = {
    statusText: string,
    showLink: boolean,
    key: 'deleted' | 'empty' | 'valid' | 'unknown' | 'error',
};

const iconStyle = {
    marginLeft: InlineStylingValues.ButtonSeparator,
}

export function GetSubmissionStatus(
    financialRecordStatus: FinancialRecordStatus | undefined, 
    commonText?: PortalTextCommon,
    financialRecordId?: string, 
    financialRecordCreated?: string
): SubmissionStatusSummary {
    switch (financialRecordStatus) {
        case 'Deleted':
            return {
                key: 'deleted',
                statusText: commonText?.submissionStatusDeleted || '',
                showLink: true
            };
        case 'Unknown':
            return {
                key: 'unknown',
                statusText: commonText?.submissionStatusUnknown || '',
                showLink: true
            };
        case 'ImportError':
            return {
                key: 'error',
                statusText: commonText?.submissionStatusImportError || '',
                showLink: true
            };
        default:
            if ((!financialRecordId) ||
                (!financialRecordCreated)) {
                return {
                    key: 'empty',
                    statusText: commonText?.submissionStatusNoDataAvailable || '',
                    showLink: true
                };
            }
            return {
                key: 'valid',
                statusText: '',
                showLink: true
            };
    }
}

export function GetStatusContent(
    brandConfig: BrandConfig, 
    submissionId: string, 
    submissionStatus: SubmissionStatusSummary,
    commonText?: PortalTextCommon,
): React.ReactElement {
    switch (submissionStatus.key) {
        case 'deleted': {
            return (
                <Tooltip title={commonText?.submissionStatusDeleted || ''}>
                    <FontAwesomeIcon style={ iconStyle } icon={faTrash} />
                </Tooltip>
            );
        }
        case 'error': {
            return (
                <Tooltip title={commonText?.submissionStatusImportError || ''}>
                    <FontAwesomeIcon style={ iconStyle } icon={faExclamationTriangle} />
                </Tooltip>
            );
        }
        case 'empty': {
            return (
                <Tooltip title={commonText?.submissionStatusNoDataAvailable || ''}>
                    <FontAwesomeIcon style={ iconStyle } icon={faThermometerEmpty} />
                </Tooltip>

            );
        }
        case 'unknown': {
            return (
                <Tooltip title={commonText?.submissionStatusUnknown || ''}>
                    <FontAwesomeIcon style={ iconStyle } icon={faQuestion} />
                </Tooltip>
            );
        }
        case 'valid': {
            return (
                <Tooltip title={commonText?.submissionStatusDataAvailable || ''}>
                    <FontAwesomeIcon style={ iconStyle } icon={faTable} />
                </Tooltip>
            );
        }
    }
    return (<></>);
}


export function GetSubmissionStatusIconColumn(
    brandConfig: BrandConfig, 
    linkMode: LinkMode,
    submissionId: string, 
    financialRecordStatus: FinancialRecordStatus | undefined, 
    commonText?: PortalTextCommon,
    financialRecordId?: string, 
    financialRecordCreated?: string
): React.ReactElement {
    const submissionStatus = GetSubmissionStatus(financialRecordStatus, commonText, financialRecordId, financialRecordCreated);
    const statusContent = GetStatusContent(brandConfig, submissionId, submissionStatus, commonText);

    return (
        <div className={'table-icon-column'}>
            <SubmittedByIcon
                linkType={linkMode}
                iconId={submissionId}
                iconStyle={iconStyle}
            />
            {statusContent}
        </div>
    );
}

export function CompareCreationTime(d1?: Date, d2?: Date): number {
    if (!(d1 && d2)) {
        // If they're both undefined they're equal.  Else if d1 is undefined it's less than d2
        return (d1 === d2) ? 0 : (!d1 ? -1 : 1);
    }

    return (d1 < d2) ? -1 : ((d1 === d2) ? 0 : 1);
}

export function CompareStringsDefaultToCreationTime(s1: string | undefined, s2: string | undefined, d1?: Date, d2?: Date): number {
    const compareS1 = s1?.toLowerCase() || '';
    const compareS2 = s2?.toLowerCase() || '';

    if (compareS1 === compareS2) {
        return CompareCreationTime(d1, d2);
    }
    return (compareS1 < compareS2) ? -1 : 1;
}

export function GetWorkspaceDisplayName(deleted: boolean, name?: string): string {
    return `${name}${deleted ? ' (deleted)' : ''}`
}

